import type { TransitionConfig } from "svelte/transition";

export interface DropFromHeaderParams {
  /** Offset the panel starts at, in px. Negative drops it in from above. */
  y: number;
  duration: number;
  easing: (t: number) => number;
}

/** Clear of the panel's box-shadow. Must match the literal in the panel's own clip-path. */
const SHADOW_BLEED_PX = 100;

/** Missing bleed still animates; it only gives up the overshoot cover. */
function readBleed(node: Element): number {
  const declared = getComputedStyle(node).getPropertyValue("--da-search-panel-bleed");
  const bleed = Number.parseFloat(declared);
  return Number.isFinite(bleed) ? bleed : 0;
}

/**
 * `fly`, but the panel clips itself so it never paints above where it comes to rest.
 *
 * It cannot be layered under the header instead: the element painting the header
 * background is one of the panel's own ancestors, and a descendant always paints above
 * its ancestor's background. Portalling out is worse — the USP banner is a sibling
 * inside `.section-header` (z-index 4 against its 10), so anything escaping the header
 * lands below the banner too.
 *
 * The moving cut stands in for the screen edge `.da-slide-over-dialog__panel--top` gets
 * for free. `--da-search-panel-bleed` is the panel above the resting line that the cut
 * gives back during `backOut`'s overshoot; read from the element so the CSS stays the
 * one place it is decided.
 */
export function dropFromHeader(
  node: Element,
  { y, duration, easing }: DropFromHeaderParams,
): TransitionConfig {
  const bleed = readBleed(node);
  return {
    duration,
    easing,
    css: (t, u) => {
      const offset = y * u;
      // Past the resting line — where `offset` turns positive — the bleed shows
      // instead of a gap.
      const cut = Math.max(0, bleed - offset);
      return `
        transform: translateY(${offset}px);
        opacity: ${t};
        clip-path: polygon(
          ${-SHADOW_BLEED_PX}px ${cut}px,
          calc(100% + ${SHADOW_BLEED_PX}px) ${cut}px,
          calc(100% + ${SHADOW_BLEED_PX}px) calc(100% + ${SHADOW_BLEED_PX}px),
          ${-SHADOW_BLEED_PX}px calc(100% + ${SHADOW_BLEED_PX}px)
        );
      `;
    },
  };
}
